// Abstract workflow / automation visualization — no stock photos, no people, no 3D.
// Pure SVG: workflow nodes + flow lines + productivity dashboard cards.
import { Bot, Calendar, Mail, FileText, BarChart3, Sparkles } from "lucide-react";

export const WorkflowVisual = () => {
  return (
    <div
      data-testid="workflow-visual"
      className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-[#E5E5E5] bg-white shadow-2xl shadow-[#0A0A0A]/10"
    >
      {/* Background grid */}
      <svg
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="grid"
            width="32"
            height="32"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 32 0 L 0 0 0 32"
              fill="none"
              stroke="#E5E5E5"
              strokeWidth="1"
            />
          </pattern>
          <linearGradient id="flowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0052FF" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#0052FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#0052FF" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" opacity="0.6" />
      </svg>

      {/* Flow lines (SVG) */}
      <svg
        viewBox="0 0 1000 560"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        {/* Input → AI core */}
        <path
          d="M 140 130 C 260 130, 320 280, 460 280"
          fill="none"
          stroke="#0052FF"
          strokeWidth="1.5"
          strokeDasharray="4 6"
          opacity="0.7"
        />
        <path
          d="M 140 280 C 260 280, 320 280, 460 280"
          fill="none"
          stroke="#0052FF"
          strokeWidth="1.5"
          strokeDasharray="4 6"
          opacity="0.7"
        />
        <path
          d="M 140 430 C 260 430, 320 280, 460 280"
          fill="none"
          stroke="#0052FF"
          strokeWidth="1.5"
          strokeDasharray="4 6"
          opacity="0.7"
        />

        {/* AI core → Outputs */}
        <path
          d="M 560 280 C 700 280, 740 130, 860 130"
          fill="none"
          stroke="#0A0A0A"
          strokeWidth="1.5"
          opacity="0.4"
        />
        <path
          d="M 560 280 C 700 280, 740 280, 860 280"
          fill="none"
          stroke="#0A0A0A"
          strokeWidth="1.5"
          opacity="0.4"
        />
        <path
          d="M 560 280 C 700 280, 740 430, 860 430"
          fill="none"
          stroke="#0A0A0A"
          strokeWidth="1.5"
          opacity="0.4"
        />

        {/* Animated pulse along main line */}
        <circle r="4" fill="#0052FF">
          <animateMotion
            dur="3.2s"
            repeatCount="indefinite"
            path="M 140 280 C 260 280, 320 280, 460 280"
          />
        </circle>
        <circle r="4" fill="#0052FF">
          <animateMotion
            dur="3.2s"
            repeatCount="indefinite"
            begin="1s"
            path="M 560 280 C 700 280, 740 280, 860 280"
          />
        </circle>
      </svg>

      {/* Input nodes (left column) */}
      <div className="absolute inset-0 flex">
        <div className="flex flex-col justify-around py-10 sm:py-14 pl-4 sm:pl-8">
          {[
            { Icon: Mail, label: "Inbox" },
            { Icon: Calendar, label: "Calendar" },
            { Icon: FileText, label: "Docs" },
          ].map(({ Icon, label }, i) => (
            <div
              key={label}
              className="flex items-center gap-2.5 bg-white border border-[#E5E5E5] rounded-xl px-3 py-2.5 shadow-sm w-fit"
              style={{ animation: `fadeUp 0.6s ${0.1 + i * 0.1}s ease-out both` }}
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#0052FF]/10 text-[#0052FF]">
                <Icon size={14} />
              </span>
              <span className="text-xs font-semibold text-[#0A0A0A]">
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* AI Core (center) */}
        <div className="flex-1 flex items-center justify-center">
          <div
            className="relative bg-[#0A0A0A] text-white rounded-2xl px-5 py-4 sm:px-6 sm:py-5 shadow-xl"
            style={{ animation: "fadeUp 0.7s 0.4s ease-out both" }}
          >
            <div className="absolute -inset-2 rounded-2xl bg-[#0052FF]/20 blur-xl -z-10" />
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#0052FF]">
                <Bot size={16} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">
                  OperatorAI
                </div>
                <div className="text-sm font-bold">Workflow Engine</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0052FF] animate-pulse" />
              <span className="text-[10px] text-white/70 font-medium">
                processing
              </span>
            </div>
          </div>
        </div>

        {/* Output cards (right column) */}
        <div className="flex flex-col justify-around py-10 sm:py-14 pr-4 sm:pr-8 items-end">
          <div
            className="bg-white border border-[#E5E5E5] rounded-xl px-3 py-2.5 shadow-sm w-fit"
            style={{ animation: "fadeUp 0.6s 0.6s ease-out both" }}
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#525252]">
              Hours Saved
            </div>
            <div className="text-lg font-extrabold text-[#0A0A0A] leading-none mt-1">
              12.4h<span className="text-[#0052FF]">/wk</span>
            </div>
          </div>

          <div
            className="bg-white border border-[#E5E5E5] rounded-xl px-3 py-2.5 shadow-sm w-fit"
            style={{ animation: "fadeUp 0.6s 0.75s ease-out both" }}
          >
            <div className="flex items-center gap-2">
              <BarChart3 size={14} className="text-[#0052FF]" />
              <div className="text-xs font-bold text-[#0A0A0A]">
                Tasks Automated
              </div>
            </div>
            <div className="mt-1.5 flex items-end gap-1 h-6">
              {[40, 60, 35, 80, 55, 95, 70].map((h, i) => (
                <span
                  key={i}
                  style={{ height: `${h}%` }}
                  className="w-1.5 bg-[#0052FF] rounded-sm"
                />
              ))}
            </div>
          </div>

          <div
            className="bg-[#0052FF] text-white rounded-xl px-3 py-2.5 shadow-sm w-fit flex items-center gap-2"
            style={{ animation: "fadeUp 0.6s 0.9s ease-out both" }}
          >
            <Sparkles size={14} />
            <span className="text-xs font-bold">Report ready</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowVisual;
